/**
 * Admin Settings Component
 * Administrative configuration page for organization-wide settings
 *
 * Access Control: Only accessible by administrators and super administrators
 *
 * Features:
 * - Comply Admin integration
 * - Auto-suspend settings
 * - MFA (Multi-Factor Authentication)
 * - Notification preferences
 */

import React, { useState, useEffect } from "react";
import {
  Switch,
  FormControlLabel,
  CircularProgress,
  TextField,
  Alert,
  Snackbar,
  Tooltip,
  IconButton,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { appVersion, BASE_URL } from "../../App_Settings/app_settings";
import { screenHeaderStyle, useSharedValue } from "../Constants/constants";
import { getUserIdFromLocalStorage } from "../../API_Endpoints/APIEndpoints";
import { useUserSettings } from "../../contexts/UserSettingsContext";
import { getOrganizationConfig } from "../../services/AdminApiService";
import "./AdminSettings.css";

interface OrganizationSettings {
  id?: string;
  name?: string;
  description?: string;
  sendToAdminAPI: boolean;
  complyAdminUrl: string;
  autoSuspendUserAfterDays: number;
}

const UserSettings = () => {
  const { isMenuOpen } = useSharedValue();
  const { refreshSettings } = useUserSettings();
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [settings, setSettings] = useState<OrganizationSettings | null>(null);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // Alert states
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<"success" | "error" | "info">("info");

  // Get current user info
  const currentUser = getUserIdFromLocalStorage();
  const userId = currentUser?.userId || "";
  const organizationId = currentUser?.organizationId || "";

  /**
   * Load user settings on component mount
   */
  useEffect(() => {
    loadUserSettings();
  }, [userId]);

  /**
   * Fetch organization settings from the API using centralized service
   */
  const loadUserSettings = async () => {
    try {
      setLoading(true);

      // Use centralized AdminApiService
      const currentOrg = await getOrganizationConfig();

      if (currentOrg) {
        const orgSettings: OrganizationSettings = {
          id: currentOrg.id || organizationId,
          name: currentOrg.name || "",
          description: currentOrg.description || "",
          sendToAdminAPI: currentOrg.sendToAdminURL === "true" ? true : false,
          complyAdminUrl: currentOrg.complyAdminUrl || "",
          autoSuspendUserAfterDays: currentOrg.autoSuspendUserAfterDays || 0,
        };
        console.log("AdminSettings: Loaded organization settings:", orgSettings);
        setSettings(orgSettings);
      } else {
        // Organization not found, set default values
        console.warn("AdminSettings: Organization not found - using default settings");
        setDefaultSettings();
      }
    } catch (error) {
      console.error("AdminSettings: Error fetching organization settings:", error);
      setDefaultSettings();
    } finally {
      setLoading(false);
    }
  };

  const setDefaultSettings = () => {
    const defaultSettings: OrganizationSettings = {
      id: organizationId,
      name: "",
      description: "",
      sendToAdminAPI: false,
      complyAdminUrl: "",
      autoSuspendUserAfterDays: 0,
    };
    setSettings(defaultSettings);
  };

  /**
   * Handle settings field changes
   */
  const handleChange = (field: keyof OrganizationSettings, value: any) => {
    if (!settings) return;

    setSettings({
      ...settings,
      [field]: value,
    });
    setHasChanges(true);
  };

  /**
   * Save settings to the database
   */
  const handleSave = async () => {
    if (!settings) return;

    try {
      setSaving(true);

      const data = {
        id: settings.id,
        userId: userId,
        name: settings.name || "",
        description: settings.description || "",
        modifiedBy: currentUser.email || currentUser.userName,
        complyAdminUrl: settings.complyAdminUrl || "",
        pythonAPI: "",
        sendToAdminAPI: settings.sendToAdminAPI ? "true" : "false",
        autoSuspendUserAfterDays: settings.autoSuspendUserAfterDays || 0,
      };

      console.log("Saving organization settings:", data);

      const response = await fetch(`${BASE_URL}/Organization`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentUser.token}`,
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        showNotification("Settings saved successfully", "success");
        setHasChanges(false);
        await loadUserSettings(); // Reload settings

        // Refresh global user settings context to update UI across all components
        await refreshSettings();
        console.log("Refreshed global user settings context");
      } else {
        showNotification("Failed to save settings", "error");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      showNotification("Error saving settings", "error");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Cancel changes and reload original settings
   */
  const handleCancel = () => {
    loadUserSettings();
    setHasChanges(false);
  };

  /**
   * Show notification message
   */
  const showNotification = (message: string, severity: "success" | "error" | "info") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setShowAlert(true);
  };

  if (loading) {
    return (
      <div style={screenHeaderStyle(isMenuOpen) as React.CSSProperties}>
        <div className="settings-loading">
          <CircularProgress size={40} style={{ color: "#2aa7e0" }} />
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div style={screenHeaderStyle(isMenuOpen) as React.CSSProperties}>
        <div className="user-settings-container">
          <Alert severity="error">Failed to load settings. Please try again.</Alert>
        </div>
      </div>
    );
  }

  return (
    <div style={screenHeaderStyle(isMenuOpen) as React.CSSProperties}>
      <div className="user-settings-container">
        {/* Header */}
        <div className="user-settings-header">
          <div>
            <h1>
              <AdminPanelSettingsIcon
                style={{ verticalAlign: "middle", marginRight: "8px", color: "#2aa7e0" }}
              />
              Admin Settings
            </h1>
            <p style={{ fontSize: "14px", color: "#666", marginTop: "4px" }}>
              Administrative configuration for organization settings
            </p>
          </div>
          <div className="settings-version">Version {appVersion}</div>
        </div>

        {/* Content */}
        <div className="user-settings-content">
          {/* Comply Admin Integration Section */}
          <div className="settings-section">
            <div className="settings-section-header">
              <div className="settings-section-icon comply-admin">
                <AdminPanelSettingsIcon />
              </div>
              <div className="settings-section-title">
                <h2>Comply Admin Integration</h2>
                <p>Configure integration with Comply Admin system</p>
              </div>
            </div>

            <div className="settings-row">
              <div className="settings-label">
                <h3>
                  Send To Admin API
                  <Tooltip title="Enable this to send data to Comply Admin API">
                    <IconButton size="small" style={{ marginLeft: "4px" }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </h3>
                <p>Enable Comply Admin API integration</p>
              </div>
              <div className="settings-control">
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sendToAdminAPI}
                      onChange={(e) => handleChange("sendToAdminAPI", e.target.checked)}
                      color="primary"
                    />
                  }
                  label={settings.sendToAdminAPI ? "Enabled" : "Disabled"}
                />
              </div>
            </div>

            {settings.sendToAdminAPI && (
              <div className="settings-row">
                <div className="settings-label">
                  <h3>Comply Admin URL</h3>
                  <p>Enter your Comply Admin instance URL</p>
                </div>
                <div className="settings-control">
                  <div className="settings-input">
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="https://admin.example.com"
                      value={settings.complyAdminUrl || ""}
                      onChange={(e) => handleChange("complyAdminUrl", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Auto-Suspend Configuration Section */}
          <div className="settings-section">
            <div className="settings-section-header">
              <div className="settings-section-icon security">
                <SecurityIcon />
              </div>
              <div className="settings-section-title">
                <h2>Account Security</h2>
                <p>Manage auto-suspend and security settings</p>
              </div>
            </div>

            <div className="settings-row">
              <div className="settings-label">
                <h3>
                  Auto-Suspend Inactive Users
                  <Tooltip title="Automatically suspend user accounts after a period of inactivity. Set to 0 to disable.">
                    <IconButton size="small" style={{ marginLeft: "4px" }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </h3>
                <p>Suspend account after period of inactivity (0 to disable)</p>
              </div>
              <div className="settings-control">
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoSuspendUserAfterDays > 0}
                      onChange={(e) => {
                        // If enabling, set to 90 days, if disabling set to 0
                        handleChange("autoSuspendUserAfterDays", e.target.checked ? 90 : 0);
                      }}
                      color="primary"
                    />
                  }
                  label={settings.autoSuspendUserAfterDays > 0 ? "Enabled" : "Disabled"}
                />
              </div>
            </div>

            {settings.autoSuspendUserAfterDays > 0 && (
              <div className="settings-row">
                <div className="settings-label">
                  <h3>Inactivity Period (Days)</h3>
                  <p>Number of days before account suspension</p>
                </div>
                <div className="settings-control">
                  <div className="settings-number-input">
                    <TextField
                      type="number"
                      size="small"
                      value={settings.autoSuspendUserAfterDays}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        handleChange("autoSuspendUserAfterDays", value > 0 ? value : 90);
                      }}
                      inputProps={{ min: 1, max: 365 }}
                    />
                  </div>
                  <span style={{ fontSize: "13px", color: "#666" }}>days</span>
                </div>
              </div>
            )}
          </div>

          {/* MFA (Multi-Factor Authentication) Section */}
          {/* <div className="settings-section">
            <div className="settings-section-header">
              <div className="settings-section-icon mfa">
                <PhoneAndroidIcon />
              </div>
              <div className="settings-section-title">
                <h2>Multi-Factor Authentication (MFA)</h2>
                <p>Add an extra layer of security to your account</p>
              </div>
            </div>

            <div className="settings-row">
              <div className="settings-label">
                <h3>
                  Enable MFA
                  <Tooltip title="Require a verification code in addition to your password when logging in">
                    <IconButton size="small" style={{ marginLeft: '4px' }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </h3>
                <p>Require two-factor authentication for login</p>
              </div>
              <div className="settings-control">
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.mfaEnabled}
                      onChange={(e) => handleChange('mfaEnabled', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={settings.mfaEnabled ? 'Enabled' : 'Disabled'}
                />
              </div>
            </div>

            {settings.mfaEnabled && (
              <>
                <div className="settings-row">
                  <div className="settings-label">
                    <h3>MFA Delivery Method</h3>
                    <p>Choose how you want to receive verification codes (uncheck both to disable MFA)</p>
                  </div>
                  <div className="settings-control">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={settings.mfaEmail}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              handleChange('mfaEmail', newValue);
                              // If both unchecked, disable MFA
                              if (!newValue && !settings.mfaMobile) {
                                handleChange('mfaEnabled', false);
                              }
                            }}
                            color="primary"
                          />
                        }
                        label="Email"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={settings.mfaMobile}
                            onChange={(e) => {
                              const newValue = e.target.checked;
                              handleChange('mfaMobile', newValue);
                              // If both unchecked, disable MFA
                              if (!newValue && !settings.mfaEmail) {
                                handleChange('mfaEnabled', false);
                              }
                            }}
                            color="primary"
                          />
                        }
                        label="Mobile / SMS"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div> */}

          {/* Notification Preferences Section */}
          {/* <div className="settings-section">
            <div className="settings-section-header">
              <div className="settings-section-icon notifications">
                <NotificationsIcon />
              </div>
              <div className="settings-section-title">
                <h2>Notification Preferences</h2>
                <p>Manage how you receive notifications</p>
              </div>
            </div>

            <div className="settings-row">
              <div className="settings-label">
                <h3>Email Notifications</h3>
                <p>Receive notifications via email</p>
              </div>
              <div className="settings-control">
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailNotifications}
                      onChange={(e) => handleChange('emailNotifications', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={settings.emailNotifications ? 'Enabled' : 'Disabled'}
                />
              </div>
            </div>

            <div className="settings-row">
              <div className="settings-label">
                <h3>SMS Notifications</h3>
                <p>Receive notifications via SMS</p>
              </div>
              <div className="settings-control">
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.smsNotifications}
                      onChange={(e) => handleChange('smsNotifications', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={settings.smsNotifications ? 'Enabled' : 'Disabled'}
                />
              </div>
            </div>
          </div> */}
        </div>

        {/* Action Buttons */}
        <div className="settings-actions">
          <button className="btn-cancel" onClick={handleCancel} disabled={!hasChanges || saving}>
            Cancel
          </button>
          <button className="btn-save" onClick={handleSave} disabled={!hasChanges || saving}>
            {saving ? (
              <>
                <CircularProgress size={16} style={{ color: "white", marginRight: "8px" }} />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>

        {/* Success/Error Snackbar */}
        <Snackbar
          open={showAlert}
          autoHideDuration={6000}
          onClose={() => setShowAlert(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setShowAlert(false)}
            severity={alertSeverity}
            sx={{ width: "100%" }}
          >
            {alertMessage}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default UserSettings;
